/**
 * A Page Layer: one ordering with its knobs and thresholds, a share of every
 * page, and the audience blocks pinned inside it.
 *
 * A feed divides its page between up to ten of these, stacked in the Feed's
 * Page section above the rules. Each is its own ClickHouse query —
 * `feed_lenses.serve_lenses` fetches them concurrently and `feed_mix.interleave`
 * weaves them through the page in proportion — and each may carry an audience:
 * "Only show" blocks that remove posts, "Move up" blocks that reorder them, and
 * Audience Groups that say any/all.
 *
 * # Coloured by ordering
 *
 * Each ordering has its own colour (`orderingVariants`) so a page split three
 * ways reads as three things from across the canvas. The Gym's slice is a
 * different KIND of thing — Graze chooses what fills it — so it is striped
 * yellow rather than an ordering colour.
 *
 * # Position is meaning
 *
 * Top to bottom in the Page section is lane order on the wire. A post two
 * layers both select is credited to the EARLIER one, so dragging a layer above
 * another is not cosmetic; the header shows the position for that reason.
 *
 * # The share is held normalised
 *
 * Shares are weights on the wire and the serve path divides by their total, so
 * editing one layer's share refits its siblings around it (`setLayerShare`) and
 * the list always sums to 1. With one layer the control is read-only: it owns
 * the page whatever is typed.
 */

import { ChevronRightIcon } from "@radix-ui/react-icons";
import { FlaskConicalIcon } from "lucide-react";
import { use, useCallback, useRef } from "react";
import { useThrottledCallback } from "sanity";
import {
  Editor,
  RecordProps,
  T,
  TLShapeUtilCanBindOpts,
  TLStateNodeConstructor,
  useEditor,
  useValue,
} from "tldraw";
import { useSharedResizeObserver } from "use-shared-resize-observer";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  BlockHeader,
  ColorVariant,
  InfoHint,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasPercentageInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { OrderingSettings } from "@/components/canvas-editor/components/OrderingSettings";
import { ThresholdsBlock } from "@/components/canvas-editor/components/ThresholdsBlock";
import {
  LAYER_MIN_W,
  addAudienceBlock,
  addLayerShape,
  compileLayer,
  convertLegacyLens,
  getLayerShapes,
  layerChildren,
  layerFacets,
  layerFilterLeaves,
  layerIsBound,
  setLayerShare,
} from "@/components/canvas-editor/layers";
import {
  NodeDocsDescription,
  NodeDocsHeader,
} from "@/components/canvas-editor/NodeDocs";
import { orderingIcons, orderingKnobs } from "@/components/canvas-editor/ordering-settings";
import {
  BindableUtil,
  createMigrations,
  dropTargetId,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  LayerProps,
  LayerShape,
  audienceBoostType,
  audienceFilterType,
  audienceLogicType,
  feedType,
  gymLayerVariant,
  isAudienceBoostShape,
  layerChildShapes,
  layerType,
  metadataType,
  orderingVariants,
} from "@/components/canvas-editor/shapes/shapes";
import { ReadonlyEditorContext, ToolStateNode } from "@/components/canvas-editor/utils";
import {
  type AudienceOnEmpty,
  type LensOrdering,
  MAX_LENS_BLEND_ENTRIES,
  audienceOnEmptyOptions,
  describeBoostStrength,
  describeLensSetting,
  duplicateOf,
  lensCombineOptions,
  lensOrderingOptions,
  lensOrderings,
  lensToAudience,
  readableLens,
  sortOrderLabels,
} from "@/lib/lens";
import type { Thresholds } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Height of the block header, which the measured body sits beneath. */
const HEADER_H = 44;
/** Air between the body and the first pinned block. */
const BODY_GAP = 12;

/** `onEmpty`, defaulted for a layer saved before the key existed. */
function onEmptyOf(shape: LayerShape): AudienceOnEmpty {
  return shape.props.onEmpty ?? "unfiltered";
}

/** The block's colour for an ordering, or the gym stripe. */
/** The closed thresholds row: how many count gates, joined how. */
function describeThresholds(thresholds: Thresholds | null | undefined): string {
  const count = (node: unknown): number => {
    if (!node || typeof node !== "object") return 0;
    const group = node as Thresholds;
    const items = group.AND ?? group.OR;
    if (!items) return 1;
    return items.reduce<number>((n, item) => n + count(item), 0);
  };
  const n = count(thresholds);
  if (n === 0) return "none";
  const op = thresholds && "OR" in thresholds ? "any of" : "all of";
  return `${op} ${n} ${n === 1 ? "rule" : "rules"}`;
}

function layerVariant(shape: LayerShape): ColorVariant {
  if (shape.props.metadata?.color) return shape.props.metadata.color;
  if (shape.props.gym) return gymLayerVariant;
  return orderingVariants[shape.props.sort_order];
}

/**
 * Wilson Trending needs readers. It ranks by a confidence-adjusted engagement
 * rate, and a post with no recorded reader interactions has no rate — so on a
 * feed few people read, the lane can return nothing at all and its share is
 * quietly absorbed by the other layers. Measured 2026-09-16 on a mid-size
 * feed: four configurations, zero Wilson posts. Said on the node and in the
 * palette, because an empty layer otherwise reads as a bug.
 */
function WilsonNote() {
  return (
    <p className="text-muted-foreground py-3 text-xs">
      Ranks by how readers react. On a feed with few readers it may have nothing to show yet, and
      the other layers fill its share of the page.
    </p>
  );
}

function WilsonLayerDocs() {
  return (
    <div className="w-[400px]">
      <NodeDocsHeader>Wilson Trending layer</NodeDocsHeader>
      <NodeDocsDescription>
        Ranks posts by how readers react to them — likes, replies, reposts and
        quotes — adjusted for how many people have seen each post, so a post
        with 10 reactions from 20 views beats one with 12 from 1,000.
      </NodeDocsDescription>
      <NodeDocsDescription>
        It needs readers to work. On a feed few people read, this layer may show
        nothing yet; the other layers fill its share of the page until it does.
      </NodeDocsDescription>
    </div>
  );
}

function LayerDocs() {
  return (
    <div className="w-[400px]">
      <NodeDocsHeader>Page Layer</NodeDocsHeader>
      <NodeDocsDescription>
        Divide every page of your feed between up to ten layers, each with its
        own ordering, settings and share of the page. Posts are interleaved in
        proportion, not served as blocks. Drag &ldquo;Only show&rdquo; and
        &ldquo;Move up&rdquo; blocks inside a layer to give it an audience, and
        an Audience Group to say &ldquo;any of these&rdquo;.
      </NodeDocsDescription>
      <NodeDocsDescription>
        Top to bottom is the order layers are served in: a post two layers both
        select is credited to the one higher up.
      </NodeDocsDescription>
    </div>
  );
}

function GymLayerDocs() {
  return (
    <div className="w-[400px]">
      <NodeDocsHeader>Gym Layer</NodeDocsHeader>
      <NodeDocsDescription>
        A slice of every page that Graze fills with the ordering it is testing,
        keeping whichever your readers engage with most. The rest of the page is
        your own layers, untouched. When nothing is being tested the slice
        follows your main layer, so readers notice nothing. Having this layer
        is what turns the gym on; removing it turns the gym off. Its share is
        how much of the feed is experimental. One per feed.
      </NodeDocsDescription>
    </div>
  );
}

/**
 * One tool per ordering, so the palette reads "New · Trending · Blend · …"
 * in each ordering's own colour, and a creator picks the layer they mean
 * instead of adding a generic one and changing its select. Each lands in the
 * Feed's Page section, last — a layer anywhere else is not a layer of anything.
 */
interface UiToolClass extends TLStateNodeConstructor {
  uiToolDef: typeof ToolStateNode.uiToolDef;
}
export const orderingLayerTools: UiToolClass[] = lensOrderingOptions.map(({ value, label }) => {
  const id = `layer-${value}`;
  return class OrderingLayerTool extends ToolStateNode {
    static override id = id;

    static override uiToolDef = (editor: Editor) => ({
      id,
      icon: orderingIcons[value],
      label: `${label} layer`,
      defaultColor: orderingVariants[value],
      onSelect: () => editor.setCurrentTool(id),
      docs: () => (value === "wilson" ? <WilsonLayerDocs /> : <LayerDocs />),
    });

    override onEnter() {
      this.editor.setCursor({ type: "none", rotation: 0 });
    }

    override onPointerDown() {
      const added = addLayerShape(this.editor, { sort_order: value });
      if (added) this.editor.select(added.id);
      this.editor.setCurrentTool("select");
    }
  };
});
export const orderingLayerToolIds: ReadonlySet<string> = new Set(
  orderingLayerTools.map((t) => t.id),
);

const gymLayerToolId = "gym-layer";

export class GymLayerTool extends ToolStateNode {
  static override id = gymLayerToolId;

  static override uiToolDef = (editor: Editor) => ({
    id: gymLayerToolId,
    icon: FlaskConicalIcon,
    label: "Gym Layer",
    defaultColor: gymLayerVariant,
    onSelect: () => editor.setCurrentTool(gymLayerToolId),
    docs: () => <GymLayerDocs />,
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const added = addLayerShape(this.editor, { gym: true });
    if (added) {
      this.editor.select(added.id);
    } else {
      // One per feed: a second would be two experiments on one page, which the
      // allocator cannot attribute. Select the one that exists instead.
      const existing = getLayerShapes(this.editor).find((l) => l.props.gym);
      if (existing) this.editor.select(existing.id);
    }
    this.editor.setCurrentTool("select");
  }
}

export class LayerShapeUtil extends BindableUtil<LayerShape> {
  static override migrations = createMigrations(layerType);
  static override type = layerType;

  static override docs = {
    defaultColor: "amber" as const,
    title: "Page Layer",
    description: "One ordering with a share of every page, its settings and its audience.",
  };

  override getType() {
    return layerType;
  }

  // No `resultState`: Debug never evaluates a layer. See audience-filter.tsx.
  static override props: RecordProps<LayerShape> = {
    w: T.number,
    minW: T.number,
    h: T.number,
    isCollapsed: T.boolean.optional(),
    isReadOnly: T.boolean.optional(),
    metadata: metadataType,
    padding: T.object({
      top: T.number,
      left: T.number,
      right: T.number,
      bottom: T.number,
      gap: T.number,
    }),
    layerId: T.string.optional(),
    share: T.number,
    sort_order: T.literalEnum(...lensOrderings),
    settings: T.unknownObject,
    showSettings: T.boolean.optional(),
    showThresholds: T.boolean.optional(),
    // Free-form, like the Feed's own: a validator that refuses a stored value
    // white-screens the canvas.
    thresholds: T.any.optional().nullable(),
    gym: T.boolean,
    boostStrength: T.number,
    boostCombine: T.literalEnum("max", "sum"),
    // Optional in the validator, defaulted on read (`onEmptyOf`): a layer saved
    // before this key existed must still open. See the note in audience-filter.tsx.
    onEmpty: T.literalEnum("strict", "unfiltered").optional(),
    legacyLens: T.unknown.optional(),
  };

  override getDefaultProps() {
    return {
      w: LAYER_MIN_W,
      minW: LAYER_MIN_W,
      h: 190,
      padding: {
        top: 190,
        bottom: 16,
        right: 16,
        left: 16,
        gap: 14,
      },
      layerId: undefined,
      share: 1,
      sort_order: "new" as LensOrdering,
      settings: {},
      thresholds: undefined,
      gym: false,
      boostStrength: 1,
      boostCombine: "max" as const,
      // `unfiltered` is the default the EDITOR offers, though the wire has no
      // default at all: a creator who has not thought about the empty case
      // should not discover it by shipping blank feeds.
      onEmpty: "unfiltered" as AudienceOnEmpty,
      legacyLens: undefined,
      metadata: undefined,
    };
  }

  /**
   * As a container: audience blocks, groups and notes. As a child: the Feed's
   * Page section, and nothing else — never a Logic node, never another layer.
   */
  override canBind({
    fromShapeType,
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<LayerShape>) {
    if (bindingType === "arrow") return true;
    if (bindingType !== "layout") return false;
    if (fromShapeType === layerType) return layerChildShapes.includes(toShapeType);
    return fromShapeType === feedType && toShapeType === layerType;
  }

  override component(shape: LayerShape) {
    const isSelected = this.editor.getSelectedShapeIds().includes(shape.id);
    return (
      <Layer
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isSelected={isSelected}
      />
    );
  }
}

function Layer({
  shape,
  updateShape,
  isSelected,
}: {
  shape: LayerShape;
  updateShape: (shape: LayerShape, props: Partial<LayerProps>) => void;
  isSelected: boolean;
}) {
  const editor = useEditor();
  // Audiences are generally available since 2026-09-21 — every creator sees the
  // Audience row and its "+ Only show" / "+ Move up" / "+ Group" buttons. They
  // followed the `lenses` and `lens_groups` beta flags before that; see the
  // matching note beside `showAudienceTools` in CanvasEditor.tsx.
  const canAuthorAudience = true;
  const canAuthorGroups = true;
  const isReadOnly = use(ReadonlyEditorContext);

  const dropTarget = useValue("layer-drop-target", () => dropTargetId.get(), []);

  const context = useValue(
    "layer-context",
    () => {
      const layers = getLayerShapes(editor);
      const lanes = layers.map((l) => compileLayer(editor, l));
      const index = layers.findIndex((l) => l.id === shape.id);
      const children = layerChildren(editor, shape);
      return {
        bound: layerIsBound(editor, shape),
        index,
        count: layers.length,
        duplicate: index >= 0 ? duplicateOf(lanes, index) : null,
        filters: layerFilterLeaves(editor, shape).length,
        boosts: children.filter(isAudienceBoostShape).length,
        facets: layerFacets(editor, shape).size,
      };
    },
    [editor, shape.id],
  );

  const onUpdateDimensions = useCallback(
    ({ h }: Pick<Dimensions, "h">) => {
      const top = HEADER_H + h + BODY_GAP;
      const delta = top - shape.props.padding.top;
      if (delta === 0) return;
      // Measurement, not authorship — see feed.tsx: no undo step, and Save
      // must not read it as an edit.
      editor.run(
        () =>
          updateShape(shape, {
            h: shape.props.h + delta,
            padding: { ...shape.props.padding, top },
          }),
        { history: "ignore" },
      );
    },
    [editor, shape, updateShape],
  );
  // The body's border-box height is what the children stack beneath; the
  // same throttle BlockContainer uses, so a knob grid re-flowing does not
  // write a shape update per pixel.
  const bodyRef = useRef<HTMLDivElement>(null);
  const throttledUpdateDimensions = useThrottledCallback(onUpdateDimensions, 50, {});
  useSharedResizeObserver({
    ref: bodyRef,
    onUpdate: (entry) => {
      const box = entry.borderBoxSize[0];
      if (!box?.blockSize) return;
      throttledUpdateDimensions({ h: box.blockSize });
    },
  });

  const variant = layerVariant(shape);
  const legacy = readableLens(shape.props.legacyLens);
  const soloLayer = context.count <= 1;
  const atCap = context.facets >= MAX_LENS_BLEND_ENTRIES;
  const orderLabel = sortOrderLabels[shape.props.sort_order];
  // A gym layer shows no parameters: its idle ordering mirrors the main layer.
  const knobs = shape.props.gym ? [] : orderingKnobs[shape.props.sort_order];
  const showSettings = shape.props.showSettings ?? false;
  const showThresholds = shape.props.showThresholds ?? false;
  const thresholdsSummary = describeThresholds(shape.props.thresholds);
  // What the closed row says: the knobs that differ from their defaults, or
  // "defaults" — so a layer that was tuned says so without opening it.
  const tuned = knobs.filter((k) => {
    const v = shape.props.settings[k.key];
    return typeof v === "number" && v !== k.default;
  });
  const settingsSummary =
    tuned.length === 0
      ? "defaults"
      : tuned
          .slice(0, 2)
          .map((k) => `${k.label} ${String(shape.props.settings[k.key])}`)
          .join(" · ") + (tuned.length > 2 ? ` +${tuned.length - 2}` : "");
  const title = shape.props.gym ? "Gym layer" : `${orderLabel} layer`;
  const HeaderIcon = shape.props.gym ? FlaskConicalIcon : orderingIcons[shape.props.sort_order];

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        isBound={context.bound}
        pattern={shape.props.metadata?.pattern ?? (shape.props.gym ? "lines" : undefined)}
        className={cn(
          "flex flex-col",
          dropTarget === shape.id && "rounded-lg shadow-[0_0_0_4px_#0588F0]",
        )}
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={shape.props.h}
        variant={variant}
        isSelected={isSelected}
        dynamicHeight={shape.props.isCollapsed}
      >
        <BlockHeader
          variant={variant}
          description={
            shape.props.metadata?.description ||
            (shape.props.gym ? (
              <GymLayerDocs />
            ) : shape.props.sort_order === "wilson" ? (
              <WilsonLayerDocs />
            ) : (
              <LayerDocs />
            ))
          }
          descriptionVariant={shape.props.metadata?.description ? "emphasis" : "normal"}
        >
          <span className="flex min-w-0 items-center gap-2">
            {context.bound && (
              <span
                className="bg-foreground text-background rounded px-1.5 py-0.5 font-mono text-[10px] font-medium normal-case"
                title="Position on the page. A post two layers both select is credited to the higher one."
              >
                {context.index + 1}
                {context.count > 1 ? `/${context.count}` : ""}
              </span>
            )}
            <HeaderIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{shape.props.metadata?.title || title}</span>
            <span className="text-muted-foreground font-normal normal-case tracking-normal">
              {soloLayer ? "· the whole page" : `· ${Math.round(shape.props.share * 100)}% of every page`}
            </span>
          </span>
        </BlockHeader>
        {/* One frame, like Logic: the body sits directly on the block's surface
            and is measured for the children's offset. The earlier nested
            BlockContainer drew a second border and fill inside the first.
            Sections carry their own py-3 and the body no gap: Tailwind v4's
            divide-y draws each rule on a section's BOTTOM edge, so a flex gap
            put all the air below the rule and none above it — the line sat
            hard against the share input. */}
        <BlockBody
          ref={bodyRef}
          className="w-full flex-col divide-y divide-black/15 px-3 dark:divide-white/15"
        >
            {!context.bound && (
              <p className="text-muted-foreground py-3 text-xs">
                Drag this into the Feed&rsquo;s Page section to make it part of the page.
              </p>
            )}

            {/* The ordering IS the layer — title, icon and colour say it — so
                there is no "Order by" control. The gym layer has no ordering
                of its own either: while nothing is being tested its slice
                follows the feed's main layer (see idleOrderingFor). */}
            {!soloLayer && (
              <div className="flex items-center gap-2 py-3">
                <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
                  Share of every page
                  {shape.props.gym && (
                    <InfoHint label="About the gym's share">
                      <p className="max-w-xs">
                        How much of the feed is experimental: this slice of every page, for
                        every reader, is where Graze tries other orderings. The rest of the
                        page is always your own layers. A bigger slice learns faster; a
                        smaller one is gentler on readers. At 100% the whole page is the
                        gym&rsquo;s.
                      </p>
                    </InfoHint>
                  )}
                </span>
                <CanvasPercentageInput
                  aria-label="Share of the page"
                  value={shape.props.share}
                  className="w-20"
                  onValueChange={(v) => setLayerShare(editor, shape, v)}
                />
              </div>
            )}

            {!shape.props.gym && shape.props.sort_order === "wilson" && <WilsonNote />}

            {knobs.length > 0 && (
              <div className="flex flex-col gap-2 py-3">
                <CanvasButton
                  variant="ghost"
                  size="sm"
                  className="h-6 w-fit justify-start gap-1.5 px-1 font-normal"
                  aria-expanded={showSettings}
                  onClick={() => updateShape(shape, { showSettings: !showSettings })}
                >
                  <ChevronRightIcon
                    className={cn("h-3 w-3 transition-transform", showSettings && "rotate-90")}
                  />
                  <span className="text-muted-foreground text-[11px] uppercase tracking-wide">
                    Parameters
                  </span>
                  {!showSettings && (
                    <span className="text-muted-foreground truncate text-xs normal-case tracking-normal">
                      {settingsSummary}
                    </span>
                  )}
                </CanvasButton>
                {showSettings && (
                  <OrderingSettings
                    order={shape.props.sort_order}
                    values={shape.props.settings}
                    onChange={(key, value) => {
                      // An empty field means "the default", which is what an
                      // ABSENT key means on the wire.
                      const settings = { ...shape.props.settings };
                      if (value === undefined) delete settings[key];
                      else settings[key] = value;
                      updateShape(shape, { settings });
                    }}
                  />
                )}
              </div>
            )}

            {/* No thresholds and no audience on a gym layer: what fills it is
                decided by the experiment (or mirrors the main layer), and a
                gate or an audience on top would be a second experiment. */}
            {!shape.props.gym && (
            <div className="flex flex-col gap-2 py-3">
              <CanvasButton
                variant="ghost"
                size="sm"
                className="h-6 w-fit justify-start gap-1.5 px-1 font-normal"
                aria-expanded={showThresholds}
                onClick={() => updateShape(shape, { showThresholds: !showThresholds })}
              >
                <ChevronRightIcon
                  className={cn("h-3 w-3 transition-transform", showThresholds && "rotate-90")}
                />
                <span className="text-muted-foreground text-[11px] uppercase tracking-wide">
                  Thresholds
                </span>
                {!showThresholds && (
                  <span className="text-muted-foreground truncate text-xs normal-case tracking-normal">
                    {thresholdsSummary}
                  </span>
                )}
              </CanvasButton>
              {showThresholds && (
                <ThresholdsBlock
                  thresholds={shape.props.thresholds}
                  onChange={(thresholds) => updateShape(shape, { thresholds })}
                />
              )}
            </div>
            )}

            {!shape.props.gym &&
              (context.filters > 0 || context.boosts > 0 || legacy || canAuthorAudience) && (
              <div className="flex flex-col gap-1.5 py-3">
                <span className="text-muted-foreground flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
                  Audience
                  <InfoHint label="About audiences">
                    <p className="max-w-xs normal-case tracking-normal">
                      Without audience blocks everyone sees the same {orderLabel.toLowerCase()}{" "}
                      posts here. Drag an &ldquo;Only show&rdquo; or &ldquo;Move up&rdquo; block
                      in to change what reaches each reader, based on their own follows; an
                      Audience Group says &ldquo;any of these&rdquo;.
                    </p>
                  </InfoHint>
                  {canAuthorAudience && !isReadOnly && (
                    <span className="ml-auto flex items-center gap-1 normal-case tracking-normal">
                      <CanvasButton
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-xs font-normal"
                        onClick={() => {
                          const added = addAudienceBlock(editor, shape, audienceFilterType);
                          if (added) editor.select(added.id);
                        }}
                      >
                        + Only show
                      </CanvasButton>
                      <CanvasButton
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-xs font-normal"
                        onClick={() => {
                          const added = addAudienceBlock(editor, shape, audienceBoostType);
                          if (added) editor.select(added.id);
                        }}
                      >
                        + Move up
                      </CanvasButton>
                      {canAuthorGroups && (
                        <CanvasButton
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-xs font-normal"
                          onClick={() => {
                            const added = addAudienceBlock(editor, shape, audienceLogicType);
                            if (added) editor.select(added.id);
                          }}
                        >
                          + Group
                        </CanvasButton>
                      )}
                    </span>
                  )}
                </span>

                {(context.filters > 0 || context.boosts > 0) && (
                  // One row of dials, like a rule's own controls: label, control,
                  // label, control — wrapping only when the block is narrow.
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {context.filters > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground shrink-0 text-xs">
                          If nothing matches
                        </span>
                        <CanvasSimpleSelect
                          aria-label="When the filters match nothing"
                          tooltip="Filters that match nothing are common — most readers share very few authors with any given feed. One answer for the page, whatever the filters."
                          value={onEmptyOf(shape)}
                          options={audienceOnEmptyOptions}
                          className="w-48"
                          onValueChange={(v) =>
                            updateShape(shape, { onEmpty: v as AudienceOnEmpty })
                          }
                        />
                      </div>
                    )}
                    {context.boosts > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
                          Boost strength
                          <InfoHint label="What this strength does">
                            <div className="flex max-w-xs flex-col gap-2">
                              <p>
                                How far the &ldquo;Move up&rdquo; blocks in this layer may rearrange
                                each page. {describeBoostStrength(shape.props.boostStrength)}
                              </p>
                              <p>
                                Below 50% the feed&rsquo;s own order mostly holds and matching posts
                                climb a few places. From 50% up the audience decides the order of the
                                whole page. Moving up never adds or removes a post; use &ldquo;Only
                                show&rdquo; for that.
                              </p>
                              <p>
                                The percentage on a &ldquo;Move up&rdquo; block is a different dial:
                                that audience&rsquo;s weight against the other &ldquo;Move up&rdquo;
                                blocks in this layer. With a single block, leave it at 100% and use
                                this strength.
                              </p>
                            </div>
                          </InfoHint>
                        </span>
                        <CanvasPercentageInput
                          aria-label="Boost strength"
                          value={shape.props.boostStrength}
                          className="w-20"
                          onValueChange={(v) =>
                            updateShape(shape, { boostStrength: Math.min(1, Math.max(0, v)) })
                          }
                        />
                        {context.boosts > 1 && (
                          <>
                            <span className="text-muted-foreground shrink-0 text-xs">combine by</span>
                            <CanvasSimpleSelect
                              aria-label="Combine boost signals"
                              value={shape.props.boostCombine}
                              options={lensCombineOptions}
                              className="w-28"
                              onValueChange={(v) =>
                                updateShape(shape, { boostCombine: v as "max" | "sum" })
                              }
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {context.filters > 0 && onEmptyOf(shape) === "strict" && (
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Readers who share no authors with this feed see an empty page — most of them.
                  </p>
                )}

                {legacy && context.filters + context.boosts === 0 && (
                  <>
                    <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                      Older reader-lens setting
                      <InfoHint label="About the older setting">
                        <p className="max-w-xs">
                          It still works — it moves matching posts up rather than removing
                          anything. {describeLensSetting(legacy)}
                        </p>
                      </InfoHint>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <CanvasButton
                        variant="outline"
                        size="sm"
                        onClick={() => convertLegacyLens(editor, shape, lensToAudience(legacy))}
                      >
                        Convert to blocks
                      </CanvasButton>
                      <CanvasButton
                        variant="ghost"
                        size="sm"
                        onClick={() => updateShape(shape, { legacyLens: undefined })}
                      >
                        Turn off
                      </CanvasButton>
                    </div>
                  </>
                )}

                {atCap && (
                  <p className="text-muted-foreground text-xs">
                    {MAX_LENS_BLEND_ENTRIES} signals is the limit for one layer.
                  </p>
                )}
              </div>
            )}

            {context.duplicate !== null && (
              <p className="text-destructive py-3 text-xs">
                Same as layer {context.duplicate + 1}. Change a setting or the audience, or remove
                it.
              </p>
            )}

            {!soloLayer && shape.props.share === 0 && (
              <p className="text-muted-foreground py-3 text-xs">
                Off. At 0% nothing from this layer reaches the page; its settings stay here for
                when you turn it back up.
              </p>
            )}
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
